import React, { useState, useEffect } from 'react';
import { X, Plus, Home, Key, Users, User } from 'lucide-react';
import { cn } from '../utils/cn';
import { CondoType } from '../utils/terminology';
import UnitSelector, { UnitOption } from './UnitSelector';
import { ParentescoOptionsSelect } from './registration/ParentescoField';

interface UnitAssignment {
  unit_id: string;
  unit_number?: string;
  block_name?: string;
  street_name?: string;
  street_number?: string;
  relation_type: string;
  /** Optional "grau de parentesco" — a free-form kinship note, shown for any relation type.
   * Tracked per-assignment: a person with two units can have two different values. */
  parentesco?: string;
}

interface UnitAssignmentManagerProps {
  value: UnitAssignment[];
  customerId: string;
  onChange: (assignments: UnitAssignment[]) => void;
  onPendingChange?: (hasPending: boolean) => void;
  darkMode?: boolean;
  condoType?: CondoType;
}

const relationTypeOptions = [
  { value: 'proprietario', label: 'Prop.', fullLabel: 'Proprietário', icon: Home },
  { value: 'inquilino', label: 'Inq.', fullLabel: 'Inquilino', icon: Key },
  { value: 'dependente', label: 'Dep.', fullLabel: 'Dependente', icon: Users },
  { value: 'familiar', label: 'Fam.', fullLabel: 'Familiar', icon: User },
];

export const UnitAssignmentManager: React.FC<UnitAssignmentManagerProps> = ({
  value,
  customerId,
  onChange,
  onPendingChange,
  darkMode = false,
  condoType,
}) => {
  const [selectedUnit, setSelectedUnit] = useState<UnitOption | null>(null);
  const [selectedRelationType, setSelectedRelationType] = useState('proprietario');
  const [selectedParentesco, setSelectedParentesco] = useState('');

  // Notify parent whenever pending state changes
  useEffect(() => {
    onPendingChange?.(!!selectedUnit);
  }, [selectedUnit, onPendingChange]);

  const handleAddAssignment = () => {
    if (!selectedUnit) return;

    // Check if already exists
    if (value.some(a => a.unit_id === selectedUnit.value)) return;

    const newAssignment: UnitAssignment = {
      unit_id: selectedUnit.value,
      unit_number: selectedUnit.unitNumber,
      block_name: selectedUnit.blockName,
      street_name: selectedUnit.streetName,
      street_number: selectedUnit.streetNumber,
      relation_type: selectedRelationType,
      parentesco: selectedParentesco || undefined,
    };

    onChange([...value, newAssignment]);
    setSelectedUnit(null);
    setSelectedRelationType('proprietario');
    setSelectedParentesco('');
  };

  const handleRemoveAssignment = (unitId: string) => {
    onChange(value.filter(a => a.unit_id !== unitId));
  };

  const handleChangeRelationType = (unitId: string, relationType: string) => {
    onChange(value.map(a =>
      a.unit_id === unitId ? { ...a, relation_type: relationType } : a
    ));
  };

  const handleChangeParentesco = (unitId: string, parentesco: string) => {
    onChange(value.map(a =>
      a.unit_id === unitId ? { ...a, parentesco: parentesco || undefined } : a
    ));
  };

  return (
    <div className="space-y-3">
      {/* Existing Assignments */}
      {value.length > 0 && (
        <div className="space-y-2">
          {value.map((assignment) => (
            <div
              key={assignment.unit_id}
              className={cn(
                'flex items-center gap-2 p-2 rounded-lg border',
                darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'
              )}
            >
              <div className="flex-1 flex flex-col gap-2">
                <div className={cn(
                  "font-medium text-sm",
                  !assignment.unit_number && "italic",
                  darkMode ? (assignment.unit_number ? "text-gray-100" : "text-gray-400") : (assignment.unit_number ? "text-gray-900" : "text-gray-500")
                )}>
                  {(() => {
                    const parts: string[] = [];
                    if (assignment.block_name) parts.push(assignment.block_name);
                    if (assignment.street_name) {
                      parts.push(assignment.street_number ? `${assignment.street_name}, ${assignment.street_number}` : assignment.street_name);
                    }
                    parts.push(assignment.unit_number || 'N/A');
                    return parts.join(' – ');
                  })()}
                </div>
                {/* Segmented Control for Relation Type */}
                <div className={cn(
                  "inline-flex rounded-lg p-0.5 w-fit",
                  darkMode ? "bg-gray-700" : "bg-gray-100"
                )}>
                  {relationTypeOptions.map((option) => {
                    const Icon = option.icon;
                    const isSelected = assignment.relation_type === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleChangeRelationType(assignment.unit_id, option.value)}
                        className={cn(
                          "flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all",
                          isSelected
                            ? darkMode
                              ? "bg-gray-800 text-white shadow-sm"
                              : "bg-white text-gray-900 shadow-sm"
                            : darkMode
                              ? "text-gray-400 hover:text-gray-200"
                              : "text-gray-600 hover:text-gray-900"
                        )}
                        title={option.fullLabel}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        <span>{isSelected ? option.fullLabel : option.label}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex flex-col gap-1 items-start">
                  <span className={cn('text-[10px] font-medium uppercase tracking-wide', darkMode ? 'text-gray-500' : 'text-gray-400')}>
                    Parentesco
                  </span>
                  <ParentescoOptionsSelect
                    value={assignment.parentesco || ''}
                    onChange={(v) => handleChangeParentesco(assignment.unit_id, v)}
                    className={cn(
                      'text-xs rounded-md px-2 py-1 w-fit',
                      darkMode ? 'bg-gray-700 border border-gray-600 text-gray-200' : 'bg-white border border-gray-200 text-gray-700'
                    )}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveAssignment(assignment.unit_id)}
                className={cn(
                  'p-1 rounded hover:bg-red-100 text-red-600',
                  darkMode && 'hover:bg-red-900/30'
                )}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add New Assignment */}
      <div className={cn(
        'p-3 rounded-lg border',
        darkMode ? 'bg-gray-800/50 border-gray-600' : 'bg-gray-50 border-gray-200'
      )}>
        <div className="mb-3">
          <UnitSelector
            customerId={customerId}
            value={selectedUnit}
            onChange={setSelectedUnit}
            condoType={condoType}
            darkMode={darkMode}
          />
        </div>

        {/* Segmented Control for Relation Type */}
        <div className="mb-3">
          <label className={cn(
            "block text-xs font-medium mb-1.5",
            darkMode ? "text-gray-300" : "text-gray-700"
          )}>
            Tipo de Relação
          </label>
          <div className={cn(
            "inline-flex rounded-lg p-0.5 w-full",
            darkMode ? "bg-gray-700" : "bg-gray-100"
          )}>
            {relationTypeOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = selectedRelationType === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setSelectedRelationType(option.value)}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all",
                    isSelected
                      ? darkMode
                        ? "bg-gray-800 text-white shadow-sm"
                        : "bg-white text-gray-900 shadow-sm"
                      : darkMode
                        ? "text-gray-400 hover:text-gray-200"
                        : "text-gray-600 hover:text-gray-900"
                  )}
                  title={option.fullLabel}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{isSelected ? option.fullLabel : option.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-3">
          <label className={cn(
            "block text-xs font-medium mb-1.5",
            darkMode ? "text-gray-300" : "text-gray-700"
          )}>
            Grau de Parentesco <span className="opacity-60">(opcional)</span>
          </label>
          <div className="flex flex-col gap-2">
            <ParentescoOptionsSelect
              value={selectedParentesco}
              onChange={setSelectedParentesco}
              className={cn(
                'w-full text-sm rounded-lg px-2.5 py-1.5',
                darkMode ? 'bg-gray-700 border border-gray-600 text-gray-200' : 'bg-white border border-gray-300 text-gray-700'
              )}
            />
          </div>
        </div>

        <button
          type="button"
          onClick={handleAddAssignment}
          disabled={!selectedUnit}
          className={cn(
            'w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors',
            'bg-blue-600 text-white hover:bg-blue-700',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          <Plus className="w-4 h-4" />
          Adicionar
        </button>
      </div>

      {value.length === 0 && (
        <div className={cn(
          'text-center py-3 text-sm',
          darkMode ? 'text-gray-400' : 'text-gray-500'
        )}>
          Nenhuma unidade atribuída. Use o formulário acima para adicionar.
        </div>
      )}
    </div>
  );
};
